import React from 'react';
import {
  FaDiscord,
  FaTwitter,
  FaYoutube,
  FaTwitch,
  FaGithub,
  FaInstagram,
} from 'react-icons/fa';
import { TeamBadgeList } from '../TeamBadge/TeamBadge';
import TeamVerifiedBadge from '../TeamBadge/TeamVerifiedBadge';
import { socialHref, TEAM_SOCIAL_KEYS, formatLastSeenLabel } from '../../config/teamBadges';
import './TeamMemberCard.css';

const SOCIAL_ICONS = {
  discord: FaDiscord,
  twitter: FaTwitter,
  youtube: FaYoutube,
  twitch: FaTwitch,
  github: FaGithub,
  instagram: FaInstagram,
};

/**
 * Card público / preview da equipe.
 */
export default function TeamMemberCard({
  member,
  style,
  className = '',
  compact = false,
}) {
  if (!member) return null;

  const socials = TEAM_SOCIAL_KEYS.map((s) => {
    const value = member.socials?.[s.id];
    if (!value) return null;
    const href = socialHref(s.id, value);
    return { ...s, value, href };
  }).filter(Boolean);

  const lastSeenLabel = formatLastSeenLabel(member.lastSeen, {
    online: Boolean(member.online),
  });

  return (
    <article
      className={`team-card${compact ? ' team-card--compact' : ''}${member.online ? ' is-online' : ''}${className ? ` ${className}` : ''}`}
      style={style}
    >
      <div className="team-card__aura" aria-hidden="true" />
      <div className="team-card__avatar-wrap">
        {member.avatarUrl ? (
          <img
            src={member.avatarUrl}
            alt=""
            className="team-card__avatar"
            width={compact ? 72 : 96}
            height={compact ? 72 : 96}
          />
        ) : (
          <div className="team-card__avatar team-card__avatar--fallback">
            {(member.displayName || '?').slice(0, 1).toUpperCase()}
          </div>
        )}
        {member.online && (
          <span className="team-card__online" title="Online no servidor" aria-label="Online" />
        )}
      </div>
      <div className="team-card__info">
        <h2 className="team-card__name">
          <span>{member.displayName || 'Membro'}</span>
          {member.verified && (
            <TeamVerifiedBadge
              size={compact ? 'sm' : 'md'}
              tooltipPlacement="bottom"
            />
          )}
        </h2>
        <p className="team-card__title">{member.title || 'Staff'}</p>
        {lastSeenLabel && (
          <p
            className={`team-card__seen${member.online ? ' is-online' : ''}`}
            title={member.lastSeen ? new Date(member.lastSeen).toLocaleString('pt-BR') : undefined}
          >
            {lastSeenLabel}
          </p>
        )}
        <TeamBadgeList
          badges={member.badges}
          size="sm"
          className="team-card__badges"
        />
        {member.bio ? <p className="team-card__bio">{member.bio}</p> : null}
        {member.discordId && (
          <p className="team-card__discord">
            <FaDiscord aria-hidden="true" />
            {member.discordUser?.username
              ? `@${member.discordUser.username}`
              : member.socials?.discord || member.discordId}
          </p>
        )}
        {socials.length > 0 && (
          <div className="team-card__socials">
            {socials.map((s) => {
              const Icon = SOCIAL_ICONS[s.id] || FaDiscord;
              if (s.href) {
                return (
                  <a
                    key={s.id}
                    href={s.href}
                    className="team-card__social"
                    target="_blank"
                    rel="noopener noreferrer"
                    title={s.label}
                    aria-label={s.label}
                  >
                    <Icon />
                  </a>
                );
              }
              return (
                <span key={s.id} className="team-card__social is-static" title={`${s.label}: ${s.value}`}>
                  <Icon />
                </span>
              );
            })}
          </div>
        )}
      </div>
    </article>
  );
}
